import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SelectMxImageLibraryDialogComponent } from 'app/library/image-library/dialogs/select-mx-image/select-mx-image.component';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IMaxBrainEnvironment } from 'app/projects/shared/src/lib/interfaces/environment.interface';
import { ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import * as moment from 'moment';
import { Article } from '../../../../article/models/article.model';
import { MaxbrainImage } from '../../../../image-library/models/maxbrain-image.model';
import { PostingType } from '../../../models/posting-type.model';
import { Posting } from '../../../models/posting.model';
import { PostingTypeApiService } from '../../../services/posting-type.api-service';

@Component({
    selector: 'app-grow-article-new-posting-dialog',
    templateUrl: './grow-article-new-posting-dialog.component.html',
    styleUrls: ['./grow-article-new-posting-dialog.component.scss'],
})
export class GrowArticleNewPostingDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    // Dialog to open Mx Image Library
    imageLibraryDialogRef: MatDialogRef<SelectMxImageLibraryDialogComponent>;

    // image Selected with MxImageLib
    maxBrainImageSelected: MaxbrainImage;

    @Input()
    articleData: Article;

    @Input('postingType')
    postingType: PostingType;

    // Create Angular Form
    private _formGroup: FormGroup;

    public controls = null;
    unsplashViewsBaseUrl: string;

    get formGroup(): FormGroup {
        return this._formGroup;
    }

    get formTypes(): PostingType[] {
        return this._postingTypes;
    }

    get articleId() {
        return this._formGroup.get('articleId');
    }

    private _postingTypes: PostingType[];

    minDate: Date;

    updateEndDate(): void {
        const startDate = this._formGroup.get('validFrom').value;

        this.minDate = startDate ? new Date(startDate) : null;
    }

    constructor(
        @Inject(MAXBRAIN_ENVIRONMENT) private _environment: IMaxBrainEnvironment,
        @Inject(MAXBRAIN_LANGUAGE_SERVICE) public languageService: ILanguageService,
        public dialogRef: MatDialogRef<GrowArticleNewPostingDialogComponent>,
        private _formBuilder: FormBuilder,
        private _matDialog: MatDialog,
        private _postingTypeApiService: PostingTypeApiService
    ) {
        super();

        this.unsplashViewsBaseUrl = this._environment.unsplashViewsBaseUrl;
        this.articleData = new Article();
    }

    async ngOnInit(): Promise<void> {
        if (this.articleData.image) {
            this.maxBrainImageSelected = this.articleData.image;
        }

        // Replace "engage-home-screen" with "grow"
        const originalUrl = this.articleData.url || '';
        const modifiedUrl = originalUrl.replace('engage-home-screen', 'grow');

        const firstTranslation = this.articleData.translations?.[0] ?? ({} as any);

        this._formGroup = this._formBuilder.group({
            postingType: this._formBuilder.group({
                type: new FormControl(this.postingType ? this.postingType.id : null, Validators.required),
            }),
            translations: this._formBuilder.group({
                title: new FormControl(firstTranslation.title, Validators.required), // todo: change translations in translation
                description: new FormControl(firstTranslation.description, Validators.required), // todo: change translations in translation
                language: new FormControl(firstTranslation.language, Validators.required),
            }),
            url: new FormControl({ value: modifiedUrl, disabled: true }),
            img: new FormControl(this.articleData.image, Validators.required),
            validFrom: new FormControl(this.articleData.validFrom || moment(), Validators.required),
            validTo: new FormControl(this.articleData.validTo),
            kind: new FormControl(this.articleData.kind),
            articleId: new FormControl(this.articleData.id, Validators.required),
        });

        this.controls = {
            type: this._formGroup.get('postingType.name'),
            title: this._formGroup.get('translations.title'),
            description: this._formGroup.get('translations.description'),
            language: this._formGroup.get('translations.language'),
            url: this._formGroup.get('url'),
            img: this._formGroup.get('img'),
            validFrom: this._formGroup.get('validFrom'),
            validTo: this._formGroup.get('validFrom'),
            kind: this._formGroup.get('kind'),
        };

        this._postingTypes = await this._postingTypeApiService.getPostingTypes();
    }

    createPosting(): void {
        const formRawData = this._formGroup.getRawValue();
        const posting = new Posting(null, formRawData);

        // load data from Posting Type
        const postingTypeRaw = this._postingTypes.find((postType) => postType.id === posting.postingType.id);
        posting.postingType = new PostingType(null, postingTypeRaw);
        // load image
        posting.image = this.maxBrainImageSelected;

        this.dialogRef.close(posting);
    }

    public openImageLibraryDialog(): void {
        this.imageLibraryDialogRef = this._matDialog.open(SelectMxImageLibraryDialogComponent, {
            width: '1000px',
            minHeight: 'calc(100vh - 230px)',
            height: 'auto',
            disableClose: true,
        });

        // Keep the same 420:240 aspect ratio, but export higher resolution for sharper images.
        this.imageLibraryDialogRef.componentInstance.previewImageHeight = 720;
        this.imageLibraryDialogRef.componentInstance.previewImageWidth = 1260;

        this.imageLibraryDialogRef.afterClosed().subscribe((image: MaxbrainImage) => {
            if (image) {
                this.maxBrainImageSelected = new MaxbrainImage(null, image);
                this._formGroup.get('img').setValue(this.maxBrainImageSelected);
                this.formGroup.markAsDirty();
            }
        });
    }
}
